import type {
  ReferenceRegenerationMode,
  ReferenceUiImageLoadingState,
  ReferenceUiRegenerateAllReferenceDraft,
  ReferenceUiRegenerationState,
  ReferenceUiSession,
} from './types';
import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import {
  buildSessionFromReferenceImages,
  compactReferenceUiSessionForPersist,
  type SetImagesOptions,
} from './helper';
import { ReferenceRegenerationScope, ReferenceUiSessionStep as SessionStep } from './types';

interface StartRegenerationParams {
  image: ReferenceImage;
  requestId: string;
  mode: ReferenceRegenerationMode;
  scope: ReferenceRegenerationScope;
  payload?: ReferenceUiRegenerationState['payload'];
  assistantMessageParts?: ReferenceUiRegenerationState['assistantMessageParts'];
}

interface FailRegenerationParams {
  requestId: string;
  error: string;
}

interface FinishRegenerationParams {
  requestId: string;
  images: ReferenceImage[];
  selectedImage: ReferenceImage | undefined;
  mode: ReferenceRegenerationMode | undefined;
  scope: ReferenceRegenerationScope | undefined;
}

const REFERENCE_UI_STORAGE_KEY = 'reference-ui-sessions';
const MAX_PERSISTED_PROJECT_COUNT = 3;

function hasImage(images: ReferenceImage[], image?: ReferenceImage): boolean {
  if (!image?.url) {
    return false;
  }
  return images.some(item => item.url === image.url);
}

function getImagesUpdatedAt(images: ReferenceImage[], fallback?: number): number | undefined {
  return images.some(image => Boolean(image.url)) ? Date.now() : fallback;
}

function omitProject<T>(record: Record<string, T>, projectId: string): Record<string, T> {
  if (!(projectId in record)) {
    return record;
  }
  const next = { ...record };
  delete next[projectId];
  return next;
}

function getSessionUpdatedAt(session: ReferenceUiSession): number {
  return session.imagesUpdatedAt ?? 0;
}

function prunePersistedSessions(
  sessionsByProjectId: Record<string, ReferenceUiSession>,
): Record<string, ReferenceUiSession> {
  return Object.fromEntries(
    Object.entries(sessionsByProjectId)
      .filter(([, session]) => session.images.some(image => Boolean(image.url)))
      .sort(([, a], [, b]) => getSessionUpdatedAt(b) - getSessionUpdatedAt(a))
      .slice(0, MAX_PERSISTED_PROJECT_COUNT),
  );
}

function compactPersistedSessions(
  sessionsByProjectId: Record<string, ReferenceUiSession>,
): Record<string, ReferenceUiSession> {
  return Object.fromEntries(
    Object.entries(prunePersistedSessions(sessionsByProjectId))
      .map(([projectId, session]) => [
        projectId,
        compactReferenceUiSessionForPersist(session),
      ] as const)
      .filter((entry): entry is readonly [string, ReferenceUiSession] => Boolean(entry[1])),
  );
}

function isStorageQuotaError(error: unknown): boolean {
  return Boolean(
    error
    && typeof error === 'object'
    && 'name' in error
    && (
      error.name === 'QuotaExceededError'
      || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    ),
  );
}

const referenceUiStorage: StateStorage = {
  getItem: name => localStorage.getItem(name),
  removeItem: name => localStorage.removeItem(name),
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
      return;
    } catch (error) {
      if (!isStorageQuotaError(error)) {
        throw error;
      }
    }

    // Replace a legacy oversized snapshot with the compact payload. If the
    // browser quota is exhausted by other keys too, keep the in-memory state
    // alive instead of aborting the selection/generation flow.
    try {
      localStorage.removeItem(name);
      localStorage.setItem(name, value);
    } catch {
      try {
        localStorage.removeItem(name);
      } catch {
        // Storage can also be unavailable in privacy-restricted contexts.
      }
    }
  },
};

interface ReferenceUiState {
  sessionsByProjectId: Record<string, ReferenceUiSession>;
  imageLoadingByProjectId: Record<string, ReferenceUiImageLoadingState>;
  regenerationByProjectId: Record<string, ReferenceUiRegenerationState>;
  regenerateAllReferenceDraftByProjectId: Record<string, ReferenceUiRegenerateAllReferenceDraft>;
  previewFocusByProjectId: Record<string, { index: number; nonce: number }>;

  // 具名 action
  setImages: (projectId: string, images: ReferenceImage[], options?: SetImagesOptions) => void;
  clearImageLoading: (projectId: string) => void;
  setSidePanelOpen: (projectId: string, open: boolean) => void;
  selectImage: (projectId: string, image: ReferenceImage) => void;
  clearSelection: (projectId: string) => void;
  confirmSelection: (projectId: string) => void;
  startRegeneration: (projectId: string, params: StartRegenerationParams) => void;
  failRegeneration: (projectId: string, params: FailRegenerationParams) => void;
  finishRegeneration: (projectId: string, params: FinishRegenerationParams) => boolean;
  clearRegeneration: (projectId: string, scope?: ReferenceRegenerationScope) => void;
  setRegenerateAllReferenceDraft: (projectId: string, draft: ReferenceUiRegenerateAllReferenceDraft | undefined) => void;
  markRegenerateAllReferenceUsed: (projectId: string) => void;

  focusPreviewImage: (projectId: string, index: number) => void;
}

export const useReferenceUiStore = create<ReferenceUiState>()(
  persist(
    set => ({
      sessionsByProjectId: {},
      imageLoadingByProjectId: {},
      regenerationByProjectId: {},
      regenerateAllReferenceDraftByProjectId: {},
      previewFocusByProjectId: {},

      setSidePanelOpen: (projectId, open) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }

          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                sidePanelOpen: open,
              },
            },
          };
        });
      },

      setImages: (projectId, images, options) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          const nextSession = buildSessionFromReferenceImages(existing, images, options);
          const nextImageLoadingByProjectId = { ...state.imageLoadingByProjectId };
          const nextRegenerationByProjectId = { ...state.regenerationByProjectId };
          const nextRegenerateAllReferenceDraftByProjectId = { ...state.regenerateAllReferenceDraftByProjectId };
          const hasGeneratedImage = images.some(image => Boolean(image.url));
          const hasIncomingMetadata = images.length > 0;
          const isLoading = !hasGeneratedImage && Boolean(options?.loading || hasIncomingMetadata);

          if (isLoading) {
            nextImageLoadingByProjectId[projectId] = { loading: true };
          } else {
            delete nextImageLoadingByProjectId[projectId];
          }

          if (!nextSession) {
            const hasImageLoadingChanged = isLoading !== Boolean(state.imageLoadingByProjectId[projectId]);
            return hasImageLoadingChanged ? {
              imageLoadingByProjectId: nextImageLoadingByProjectId,
            } : state;
          }
          const isNewImageSet = existing && existing.images !== nextSession.images;
          if (isNewImageSet) {
            delete nextRegenerationByProjectId[projectId];
            delete nextRegenerateAllReferenceDraftByProjectId[projectId];
          }

          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: nextSession,
            },
            imageLoadingByProjectId: nextImageLoadingByProjectId,
            regenerationByProjectId: nextRegenerationByProjectId,
            regenerateAllReferenceDraftByProjectId: nextRegenerateAllReferenceDraftByProjectId,
          };
        });
      },

      clearImageLoading: (projectId) => {
        set(state => ({
          imageLoadingByProjectId: omitProject(state.imageLoadingByProjectId, projectId),
        }));
      },

      selectImage: (projectId, image) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                selectedImage: image,
                step: SessionStep.ReferenceFeedback,
              },
            },
            regenerationByProjectId: omitProject(state.regenerationByProjectId, projectId),
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
      },

      clearSelection: (projectId) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                selectedImage: undefined,
                step: SessionStep.StyleSelect,
              },
            },
            regenerationByProjectId: omitProject(state.regenerationByProjectId, projectId),
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
      },

      confirmSelection: (projectId) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          if (!existing.selectedImage?.url) {
            return state;
          }
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                step: SessionStep.ReferenceFeedbackConfirmed,
              },
            },
            regenerationByProjectId: omitProject(state.regenerationByProjectId, projectId),
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
      },

      startRegeneration: (projectId, params) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          const isSingleScope = params.scope === ReferenceRegenerationScope.Single;
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                selectedImage: isSingleScope ? params.image : undefined,
                step: isSingleScope ? SessionStep.ReferenceFeedback : SessionStep.StyleSelect,
              },
            },
            regenerationByProjectId: {
              ...state.regenerationByProjectId,
              [projectId]: {
                status: 'loading',
                requestId: params.requestId,
                mode: params.mode,
                scope: params.scope,
                referenceImage: params.image,
                payload: params.payload,
                assistantMessageParts: params.assistantMessageParts,
              },
            },
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
      },

      failRegeneration: (projectId, params) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          const existingRegeneration = state.regenerationByProjectId[projectId];
          if (existingRegeneration?.requestId !== params.requestId) {
            return state;
          }
          return {
            regenerationByProjectId: {
              ...state.regenerationByProjectId,
              [projectId]: {
                ...existingRegeneration,
                status: 'failed',
                error: params.error,
              },
            },
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
      },

      finishRegeneration: (projectId, params) => {
        let didFinish = false;
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          const existingRegeneration = state.regenerationByProjectId[projectId];
          if (existingRegeneration?.requestId !== params.requestId) {
            return state;
          }
          didFinish = true;
          const isSingleScope = (params.scope ?? existingRegeneration.scope) === ReferenceRegenerationScope.Single;
          const regeneratedImage = params.selectedImage ?? (isSingleScope ? params.images[0] : undefined);
          const nextImages =
            isSingleScope && regeneratedImage?.url
              ? existing.images.map(image => (image.url === existing.selectedImage?.url ? regeneratedImage : image))
              : params.images;
          const nextSelectedImage = hasImage(nextImages, regeneratedImage) ? regeneratedImage : undefined;
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                images: nextImages,
                imagesUpdatedAt: getImagesUpdatedAt(nextImages, existing.imagesUpdatedAt),
                selectedImage: nextSelectedImage,
                step: nextSelectedImage ? SessionStep.ReferenceFeedback : SessionStep.StyleSelect,
              },
            },
            regenerationByProjectId: omitProject(state.regenerationByProjectId, projectId),
            regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
          };
        });
        return didFinish;
      },

      clearRegeneration: (projectId, scope) => {
        set(state => {
          const existingRegeneration = state.regenerationByProjectId[projectId];
          if (!existingRegeneration) {
            return state;
          }
          if (scope && existingRegeneration.scope !== scope) {
            return state;
          }
          return {
            regenerationByProjectId: omitProject(state.regenerationByProjectId, projectId),
          };
        });
      },

      setRegenerateAllReferenceDraft: (projectId, draft) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          if (!draft) {
            return {
              regenerateAllReferenceDraftByProjectId: omitProject(state.regenerateAllReferenceDraftByProjectId, projectId),
            };
          }
          return {
            regenerateAllReferenceDraftByProjectId: {
              ...state.regenerateAllReferenceDraftByProjectId,
              [projectId]: draft,
            },
          };
        });
      },

      markRegenerateAllReferenceUsed: (projectId) => {
        set(state => {
          const existing = state.sessionsByProjectId[projectId];
          if (!existing) {
            return state;
          }
          return {
            sessionsByProjectId: {
              ...state.sessionsByProjectId,
              [projectId]: {
                ...existing,
                regenerateAllReferenceUsed: true,
              },
            },
          };
        });
      },

      focusPreviewImage: (projectId, index) => {
        set(state => {
          const currentNonce = state.previewFocusByProjectId[projectId]?.nonce ?? 0;
          return {
            previewFocusByProjectId: {
              ...state.previewFocusByProjectId,
              [projectId]: {
                index,
                nonce: currentNonce + 1,
              },
            },
          };
        });
      },
    }),
    {
      name: REFERENCE_UI_STORAGE_KEY,
      storage: createJSONStorage(() => referenceUiStorage),
      partialize: state => ({
        sessionsByProjectId: compactPersistedSessions(state.sessionsByProjectId),
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        sessionsByProjectId: prunePersistedSessions(
          (persistedState as Partial<ReferenceUiState> | undefined)?.sessionsByProjectId ?? {},
        ),
      }),
    },
  ),
);
